import React, { useMemo } from "react";
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { FileDownload } from "@mui/icons-material";
import { useWarehouseLabelPdf, useWarehouseLabelPreview } from "hooks/useWarehouseLabel";
import {
  LabelPrintControls,
  LabelPrintHeader,
  LabelPrintProvider,
  LabelPrintStatus,
  withCopies,
} from "pages/WarehouseLabels/PrintPanel";
import { DEFAULT_BAND_COLOR } from "utils/warehouseLabels";

const TAPE_WIDTH_IN = 1;
const LABEL_LENGTH_IN = 3.5;

// One location label: preview on the left, printer controls on the right.
export function LocationLabelPrint({ code }: { code: string }) {
  const trimmed = code.trim();
  // The band color comes from the location type. Only the fallback gold is sent here.
  const settings = useMemo(() => ({ default_color: DEFAULT_BAND_COLOR }), []);
  const payload = useMemo(
    () =>
      trimmed
        ? {
            kind: "location",
            codes: [trimmed],
            tape_width_in: TAPE_WIDTH_IN,
            label_length_in: LABEL_LENGTH_IN,
            autosize: true,
            show_qr: true,
            settings,
          }
        : null,
    [trimmed, settings],
  );
  const { imageUrl, error, isLoading } = useWarehouseLabelPreview(payload, 0);
  const { downloadPdf, isLoadingPdf, pdfError } = useWarehouseLabelPdf();

  return (
    <LabelPrintProvider kind="location" payload={payload}>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mt: 1 }}>
        <LabelPrintHeader />
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="stretch" sx={{ mt: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            {trimmed && (
              <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Label preview · {LABEL_LENGTH_IN} × {TAPE_WIDTH_IN} in
              </Typography>
            )}
            <Box
              sx={{
                minHeight: 140,
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "background.paper",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              {!trimmed ? (
                <Typography variant="body2" color="text.secondary">
                  Enter a location name to print its label.
                </Typography>
              ) : error && !isLoading ? (
                <Alert severity="warning">{error}</Alert>
              ) : imageUrl ? (
                <Box
                  component="img"
                  src={imageUrl}
                  alt={`Label for ${trimmed}`}
                  sx={{ maxWidth: "100%", maxHeight: 240, bgcolor: "common.white", opacity: isLoading ? 0.5 : 1 }}
                />
              ) : (
                <CircularProgress size={32} />
              )}
            </Box>
          </Box>
          <Box
            sx={{
              width: { xs: "100%", md: 440 },
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <LabelPrintControls>
              {(copies) => (
                <Button
                  type="button"
                  variant="outlined"
                  fullWidth
                  startIcon={<FileDownload />}
                  disabled={!payload || isLoadingPdf}
                  onClick={() => payload && downloadPdf(withCopies(payload, copies))}
                >
                  {isLoadingPdf ? "Making PDF…" : "Download PDF"}
                </Button>
              )}
            </LabelPrintControls>
          </Box>
        </Stack>
        {pdfError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {pdfError}
          </Alert>
        )}
        <LabelPrintStatus />
      </Paper>
    </LabelPrintProvider>
  );
}
